import {useSyncExternalStore} from 'react';
import {offlineState,subscribeOffline} from '../lib/offlineVault';
// Start a new encounter. Flow:
//   1. Look up the patient by E.164 phone (POST /patient-lookup).
//   2. Pick encounter type + optional chief complaint.
//   3. POST /encounters → navigate into the detail screen.
//
// Consent handling: encounters require an active grant. If the POST comes
// back with 403 consent_required we surface a "Request access" button that
// fires /patients/:phone/request-access with data_categories=['documents']
// (mirrors the /digitize consent gate). Patient acks on WhatsApp; clinician
// retries the create.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { EncounterType } from '@pamosi/shared';
import { ApiError, encountersApi, patientsApi, type PatientLookupRes } from '../lib/api';

// Poll cadence for the "waiting on patient" state. 3s feels responsive
// without spamming the API — patients typically tap accept within a few
// seconds. We give up after 5 minutes so we're not polling forever on a
// closed tab.
const CONSENT_POLL_INTERVAL_MS = 3_000;
const CONSENT_POLL_TIMEOUT_MS = 5 * 60 * 1_000;

const PHONE_RE = /^\+[1-9]\d{7,14}$/;

// Same 7 values that the server whitelists in encounters_router.ts. Keep
// display order clinical-first (general/follow-up first, emergency last).
const ENCOUNTER_TYPE_OPTIONS: { value: EncounterType; label: string }[] = [
    { value: 'general',         label: 'General consultation' },
    { value: 'follow_up',       label: 'Follow-up visit' },
    { value: 'antenatal_visit', label: 'Antenatal visit' },
    { value: 'postnatal_visit', label: 'Postnatal visit' },
    { value: 'vaccination',     label: 'Vaccination' },
    { value: 'screening',       label: 'Screening' },
    { value: 'emergency',       label: 'Emergency' },
];

export function NewEncounterScreen() {
    const device=useSyncExternalStore(subscribeOffline,offlineState);
    const [departmentId,setDepartmentId]=useState('');
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const selectedPatientId = params.get('patient');
    const [phone, setPhone] = useState('');
    const [patient, setPatient] = useState<PatientLookupRes['patient'] | null>(null);
    // `isNew` = row was created on this lookup (walk-in, no Pamosi account).
    // Drives the badge under the name and the copy that nudges the clinician
    // to type a name before starting the encounter.
    const [isNew, setIsNew] = useState(false);
    // Editable name is prefilled from the server row and can be edited in
    // place. Empty is only allowed for existing Pamosi patients that were
    // saved without one — never for walk-ins.
    const [nameInput, setNameInput] = useState('');
    const [encounterType, setEncounterType] = useState<EncounterType>('general');
    const [chiefComplaint, setChiefComplaint] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    // `consentPending`=true means the last create attempt failed with
    // consent_required and we've shown the "Request access" button. Once
    // the clinician fires it, we flip to `consentRequested` so they know
    // to wait for the patient to reply on WhatsApp.
    const [consentPending, setConsentPending] = useState(false);
    const [consentRequested, setConsentRequested] = useState(false);
    // Group id from the /request-access response. When set, we poll the
    // status endpoint every 3s and auto-start the encounter when the
    // patient accepts (or show a declined banner when they decline).
    const [pendingRequestGroupId, setPendingRequestGroupId] = useState<string | null>(null);
    const [consentDecision, setConsentDecision] = useState<
        'declined' | 'expired' | 'timed_out' | null
    >(null);
    // Track the state values the poll loop needs without re-subscribing
    // every render — we only want the interval torn down when the
    // groupId itself changes.
    const startEncounterRef = useRef<(opts?: { bypassConsent?: boolean }) => Promise<void>>();

    useEffect(() => {
        if (!selectedPatientId) return;
        let active = true;
        patientsApi.encounterHistory(selectedPatientId).then(({patient: p}) => {
            if (!active) return;
            setPatient({...p, phone_e164: p.phone_e164 ?? '', nin_last4: null});
            setNameInput(p.full_name ?? '');
        }).catch(() => { if (active) setError('Could not load the selected clinic patient. Return to their record and try again.'); });
        return () => { active = false; };
    }, [selectedPatientId]);

    async function lookup() {
        setError(null);
        setPatient(null);
        setIsNew(false);
        setNameInput('');
        setConsentPending(false);
        setConsentRequested(false);
        setPendingRequestGroupId(null);
        setConsentDecision(null);
        // Clinicians often paste digits without the leading `+` — accept
        // that and normalize before validating so the form doesn't feel
        // silently broken.
        const raw = phone.trim();
        const normalized = raw.startsWith('+') ? raw : (raw ? `+${raw.replace(/^0+/, '')}` : '');
        if (!PHONE_RE.test(normalized)) {
            setError('Phone must be in E.164 format (e.g. +2348012345678).');
            return;
        }
        if (normalized !== raw) setPhone(normalized);
        setBusy(true);
        try {
            const res = await patientsApi.lookup(normalized);
            setPatient(res.patient);
            setIsNew(res.is_new);
            setNameInput(res.patient.full_name?.trim() ?? '');
        } catch (err) {
            console.error('patient lookup failed', err);
            const code = err instanceof ApiError ? err.code : 'unknown';
            setError(
                code === 'invalid_phone' ? 'Phone must be in E.164 format (e.g. +2348012345678).'
                : code === 'network_timeout' ? 'Lookup timed out. Check your connection and try again.'
                : code === 'network_error' ? 'Network error — could not reach the server.'
                : 'Could not look up that patient. Try again.',
            );
        } finally {
            setBusy(false);
        }
    }

    async function startEncounter(opts: { bypassConsent?: boolean } = {}) {
        if (!patient) return;
        setError(null);
        const typedName = nameInput.trim();
        // Walk-ins must have a name — the row was auto-created by lookup so
        // it starts blank. For existing rows we don't force one.
        if (isNew && !typedName) {
            setError('Enter the patient\u2019s name to start a walk-in encounter.');
            return;
        }
        setBusy(true);
        try {
            // Backfill the patient row if the clinician typed a name and the
            // server row still has none. Server never overwrites an existing
            // name, so this is safe to fire whenever they differ.
            let workingPatient = patient;
            const serverName = patient.full_name?.trim() ?? '';
            if (typedName && typedName !== serverName && !serverName) {
                const res = await patientsApi.lookup(patient.phone_e164, { fullName: typedName });
                workingPatient = res.patient;
                setPatient(res.patient);
                setIsNew(res.is_new);
            }
            const res = await encountersApi.create({
                ...(departmentId?{departmentId}:{}),
                patientId: workingPatient.id,
                encounterType,
                ...(chiefComplaint.trim() ? { chiefComplaint: chiefComplaint.trim() } : {}),
                ...(opts.bypassConsent ? { bypassConsent: true } : {}),
            });
            navigate(`/encounters/${res.encounter.id}`);
        } catch (err) {
            if (err instanceof ApiError && err.code === 'consent_required') {
                setConsentPending(true);
                setError(patient.phone_e164 ? 'This patient has not granted access yet. Request access or proceed without access.' : 'This child has no independent Health sharing grant. You can record clinic care using Proceed without access; this does not grant access to a caregiver’s Health records.');
            } else if (err instanceof ApiError && err.code === 'no_clinic_membership') {
                setError('You are not part of a clinic yet.');
            } else {
                setError('Could not start the encounter. Try again.');
            }
        } finally {
            setBusy(false);
        }
    }

    async function proceedWithoutAccess() {
        // Confirm before we skip the consent gate — this is a real
        // decision, not a "keep trying" retry. Once the encounter exists,
        // it's already in the patient's record on the server side.
        const ok = window.confirm(
            selectedPatientId ? 'Start this clinic visit without accessing Pamosi Health records? The visit uses this child’s existing patient identity. Caregiver access still requires separate authorization.' :
            'The patient has not granted access yet. Proceed and start the encounter anyway?\n\n' +
            'The visit will still be saved to their record. They\u2019ll see it in Pamosi once they grant access.',
        );
        if (!ok) return;
        await startEncounter({ bypassConsent: true });
    }

    async function requestAccess() {
        if (!patient) return;
        setError(null);
        setConsentDecision(null);
        setBusy(true);
        try {
            const res = await patientsApi.requestAccess({
                phoneE164: patient.phone_e164,
                dataCategories: ['documents'],
            });
            setConsentRequested(true);
            // Kick off polling — the useEffect below reacts to
            // pendingRequestGroupId and auto-fires startEncounter on
            // 'active' (see the "poll patient response" effect).
            setPendingRequestGroupId(res.request.request_group_id);
        } catch (err) {
            const code = err instanceof ApiError ? err.code : 'unknown';
            setError(
                code === 'not_enabled'
                    ? 'In-app consent requests are not enabled for this clinic yet. Ask the patient to grant access on WhatsApp.'
                : code === 'cannot_request_from_self'
                    // Common in testing: clinician account and patient share
                    // the phone. Also a real guard for solo practitioners.
                    ? 'You can\u2019t request access from your own phone number. Use Proceed without access if you\u2019re testing.'
                : code === 'consent_request_conflict'
                    ? 'A pending request for this patient already exists.'
                : code === 'invalid_patient_phone'
                    ? 'Patient phone is not in a valid international format.'
                : 'Could not send the consent request. Try again.',
            );
        } finally {
            setBusy(false);
        }
    }

    // Keep a ref to the latest startEncounter so the polling effect can
    // fire it on 'active' without re-installing the interval on every
    // render (startEncounter is a fresh closure each render).
    startEncounterRef.current = startEncounter;

    // Poll the request status while we're waiting on the patient. When
    // the patient taps Accept on their side, /consent/requests/:groupId
    // flips to 'active' — we auto-start the encounter so the clinician
    // doesn't have to click Retry. On decline/expired we surface a
    // banner and leave "Proceed without access" as the remaining option.
    useEffect(() => {
        if (!pendingRequestGroupId) return;
        const groupId = pendingRequestGroupId;
        let cancelled = false;
        const startedAt = Date.now();
        const tick = async () => {
            if (cancelled) return;
            try {
                const res = await patientsApi.getConsentRequestStatus(groupId);
                if (cancelled) return;
                if (res.status === 'active') {
                    // Stop polling and auto-start the encounter — consent
                    // is now granted so the create call will succeed.
                    setPendingRequestGroupId(null);
                    setConsentPending(false);
                    setConsentRequested(false);
                    setError(null);
                    await startEncounterRef.current?.();
                    return;
                }
                if (res.status === 'declined' || res.status === 'expired') {
                    setPendingRequestGroupId(null);
                    setConsentDecision(res.status);
                    return;
                }
                if (Date.now() - startedAt > CONSENT_POLL_TIMEOUT_MS) {
                    setPendingRequestGroupId(null);
                    setConsentDecision('timed_out');
                }
            } catch {
                // Transient network errors are ignored — the next tick
                // will retry. A genuine 404 (request revoked) will keep
                // returning 404 until timeout; that's acceptable.
            }
        };
        const id = window.setInterval(() => { void tick(); }, CONSENT_POLL_INTERVAL_MS);
        // Fire once immediately so a fast accept lands within a second.
        void tick();
        return () => {
            cancelled = true;
            window.clearInterval(id);
        };
    }, [pendingRequestGroupId]);

    return (
        <div className="space-y-6 max-w-lg">
            {device&&<label className="pm-label">Clinic department<select className="pm-input" value={departmentId} onChange={e=>setDepartmentId(e.target.value)}><option value="">Choose department for clinic-only visit</option>{device.departments.map(d=><option value={d.id} key={d.id}>{d.name}</option>)}</select></label>}
            <div>
                <h1 className="text-slate-900 text-2xl font-semibold">New encounter</h1>
                <p className="text-slate-500 text-sm mt-1">
                    {selectedPatientId ? 'Start a visit for the patient selected from their clinic record.' : 'Look up the patient by phone, then start the visit.'}
                </p>
            </div>

            <div className="pm-card p-5 space-y-4">
                {!selectedPatientId && <div>
                    <label className="pm-label" htmlFor="phone">Patient phone</label>
                    <div className="flex gap-2">
                        <input
                            id="phone"
                            className="pm-input flex-1"
                            type="tel"
                            inputMode="tel"
                            placeholder="+2348012345678"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            disabled={busy}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); void lookup(); }
                            }}
                        />
                        <button
                            type="button"
                            className="pm-btn-outline whitespace-nowrap"
                            onClick={() => void lookup()}
                            disabled={busy}
                        >
                            {busy && !patient ? 'Looking up…' : 'Look up'}
                        </button>
                    </div>
                </div>}

                {/* Show lookup errors here — the encounter-detail card below
                    only renders once we have a patient, so validation and
                    network errors would otherwise be invisible. */}
                {!patient && error && <div className="pm-error">{error}</div>}

                {patient && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                            <div className="text-slate-500 text-xs">
                                {patient.phone_e164 || 'Separate child identity · caregiver contact is in the child profile'}
                                {patient.phone_e164 && patient.identity_tier !== 'unverified' && ' · verified'}
                            </div>
                            {/* Walk-ins are auto-created by the lookup call; anything else
                                is an existing Pamosi patient we found on their phone. */}
                            <span
                                className={
                                    isNew
                                        ? 'rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800'
                                        : 'rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800'
                                }
                            >
                                {selectedPatientId ? 'Clinic patient' : isNew ? 'New / walk-in' : 'Pamosi patient'}
                            </span>
                        </div>
                        <div>
                            <label className="pm-label" htmlFor="patient-name">Patient name</label>
                            <input
                                id="patient-name"
                                className="pm-input"
                                type="text"
                                placeholder={isNew ? 'e.g. Adaeze Okonkwo' : 'Add a name'}
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                disabled={busy}
                                maxLength={120}
                            />
                            {isNew && (
                                <p className="text-slate-500 text-xs mt-1">
                                    This phone isn’t on Pamosi yet — enter their name to
                                    start a walk-in encounter.
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {patient && (
                <div className="pm-card p-5 space-y-4">
                    <div>
                        <label className="pm-label" htmlFor="type">Encounter type</label>
                        <select
                            id="type"
                            className="pm-input"
                            value={encounterType}
                            onChange={(e) => setEncounterType(e.target.value as EncounterType)}
                            disabled={busy}
                        >
                            {ENCOUNTER_TYPE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="pm-label" htmlFor="chief">Chief complaint (optional)</label>
                        <textarea
                            id="chief"
                            className="pm-input min-h-[80px]"
                            placeholder="e.g. Fever for 3 days, cough"
                            value={chiefComplaint}
                            onChange={(e) => setChiefComplaint(e.target.value)}
                            disabled={busy}
                        />
                    </div>

                    {error && <div className="pm-error">{error}</div>}

                    {!consentPending && (
                        <button
                            className="pm-btn-primary w-full"
                            type="button"
                            onClick={() => void startEncounter()}
                            disabled={busy}
                        >
                            {busy ? 'Starting…' : 'Start encounter'}
                        </button>
                    )}

                    {consentPending && !consentRequested && (
                        <div className="space-y-2">
                            {patient.phone_e164 && <button
                                className="pm-btn-primary w-full"
                                type="button"
                                onClick={() => void requestAccess()}
                                disabled={busy}
                            >
                                {busy ? 'Sending…' : 'Request patient access'}
                            </button>}
                            <button
                                className="pm-btn-outline w-full"
                                type="button"
                                onClick={() => void proceedWithoutAccess()}
                                disabled={busy}
                            >
                                {busy ? 'Starting…' : 'Proceed without access'}
                            </button>
                            <p className="text-slate-500 text-xs">
                                {selectedPatientId ? 'Clinic care is saved under this child’s identity. Caregiver Health access requires separate authorization.' : 'Proceeding still saves the encounter to the patient’s record. They’ll see it in Pamosi once they grant access.'}
                            </p>
                        </div>
                    )}

                    {consentRequested && !consentDecision && (
                        <div className="space-y-2">
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                                {pendingRequestGroupId ? (
                                    <>
                                        <span className="inline-flex items-center gap-2">
                                            <span
                                                className="inline-block h-2 w-2 animate-pulse rounded-full bg-emerald-500"
                                                aria-hidden="true"
                                            />
                                            Waiting for the patient to respond in the
                                            Pamosi app…
                                        </span>
                                        <div className="mt-1 text-xs text-emerald-700">
                                            The encounter will start automatically as
                                            soon as they accept.
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        Consent request sent. The patient will see it in the
                                        Pamosi app under Records access requests.
                                    </>
                                )}
                            </div>
                            <button
                                className="pm-btn-outline w-full"
                                type="button"
                                onClick={() => void proceedWithoutAccess()}
                                disabled={busy}
                            >
                                {busy ? 'Starting…' : 'Proceed without waiting'}
                            </button>
                        </div>
                    )}

                    {consentDecision === 'declined' && (
                        <div className="space-y-2">
                            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                                The patient declined the access request.
                            </div>
                            <button
                                className="pm-btn-outline w-full"
                                type="button"
                                onClick={() => void proceedWithoutAccess()}
                                disabled={busy}
                            >
                                {busy ? 'Starting…' : 'Proceed without access'}
                            </button>
                        </div>
                    )}

                    {consentDecision === 'expired' && (
                        <div className="space-y-2">
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                                The access request expired before the patient
                                responded.
                            </div>
                            <button
                                className="pm-btn-primary w-full"
                                type="button"
                                onClick={() => {
                                    setConsentRequested(false);
                                    setConsentDecision(null);
                                }}
                                disabled={busy}
                            >
                                Send a new request
                            </button>
                            <button
                                className="pm-btn-outline w-full"
                                type="button"
                                onClick={() => void proceedWithoutAccess()}
                                disabled={busy}
                            >
                                {busy ? 'Starting…' : 'Proceed without access'}
                            </button>
                        </div>
                    )}

                    {consentDecision === 'timed_out' && (
                        <div className="space-y-2">
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                                No response yet. You can keep waiting or start the
                                encounter without access.
                            </div>
                            <button
                                className="pm-btn-outline w-full"
                                type="button"
                                onClick={() => {
                                    // Resume polling on the same request.
                                    // We saved the groupId nowhere else, so
                                    // just send a fresh one — safer than
                                    // hoping the old row is still pending.
                                    setConsentRequested(false);
                                    setConsentDecision(null);
                                }}
                                disabled={busy}
                            >
                                Send a new request
                            </button>
                            <button
                                className="pm-btn-outline w-full"
                                type="button"
                                onClick={() => void proceedWithoutAccess()}
                                disabled={busy}
                            >
                                {busy ? 'Starting…' : 'Proceed without access'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
